import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import { useItemEdit } from "lib/hooks/useItemEdit";
import { AddOnMetaType, AddOnType, ItemType } from "lib/types/checkout";
import { getItemLevelAddOnsForItemIndex, hasAnyItemProperties } from "lib/utils/checkout";
import React from "react";
import { AddOnItem } from "../common/AddOnItem";
import { ItemCard } from "./ItemCard";
import { useMerchantContext } from "lib/contexts/MerchantProvider";

interface CheckoutItemsProps {
  items: ItemType[];
  disableOOSItems?: boolean;
  checkoutItemsMutable?: boolean;
  cartLevelAddOns?: AddOnType[];
  availableAddOns?: Record<string, AddOnMetaType>;
  enableAddOnActions?: boolean;
  isUnservicable?: boolean;
  blockVarientSelector?: boolean;
}

const CheckoutItems: React.FC<CheckoutItemsProps> = React.memo(
  ({
    items,
    disableOOSItems = false,
    checkoutItemsMutable = false,
    cartLevelAddOns,
    availableAddOns,
    enableAddOnActions = true,
    isUnservicable = false,
    blockVarientSelector = false,
  }) => {
    const { handleAddOnEdit, showFullScreenLoader } = useItemEdit();

    const merchantContext = useMerchantContext();
    const {
      state: { checkoutMetadata, cartMetadata },
    } = merchantContext;

    const layoutMeta =
      checkoutMetadata?.uiAttributes?.layout?.metadata ?? cartMetadata?.uiAttributes?.layout?.metadata;
    const itemPropertiesConfig =
      checkoutMetadata?.uiAttributes?.itemProperties ?? cartMetadata?.uiAttributes?.itemProperties;

    const isItemPropertiesAvailable = hasAnyItemProperties(
      items,
      itemPropertiesConfig ?? [],
      Boolean(layoutMeta?.showAllItemProperties),
    );

    return (
      <>
        <ul className={`flex w-full flex-col ${isUnservicable ? "space-y-0" : "space-y-3"}`}>
          {!isUnservicable &&
            cartLevelAddOns?.map((item) => {
              return (
                <li key={item?.addon_id} className="py-1.5 px-3">
                  <AddOnItem
                    variant="CHECKOUT"
                    addOnData={availableAddOns![item?.addon_id]}
                    allowDelete={enableAddOnActions}
                    disableActions={!enableAddOnActions}
                    handleAddOnDelete={
                      enableAddOnActions
                        ? () =>
                            handleAddOnEdit({
                              action: "DELETE",
                              addOnLevel: "CART",
                              addOnId: item?.addon_id,
                              showFullScreenLoader: true,
                              addOnPrice: item?.price,
                            })
                        : undefined
                    }
                    isItemPropertiesAvailable={Boolean(isItemPropertiesAvailable)}
                  />
                </li>
              );
            })}
          {(() => {
            // item_id is this item's positional slot in the current response (equal to its own
            // array index), not a stable per-product identifier - keying on it makes React reuse
            // a card's fiber (and its internal refs, eg. the add-on auto-attach guards) for a
            // completely different product whenever the cart's item order shifts, which happens
            // on every add/remove. variant_id stays constant for a given line across responses,
            // so key on that instead - falling back to the old scheme only to disambiguate the
            // rare case of two lines sharing a variant_id (eg. unmerged bundle components),
            // where nothing here is stable across a reorder anyway.
            const variantOccurrence: Record<string, number> = {};
            return items?.map((item: ItemType, index: number) => {
              const variantKey = item.variant_id ?? "";
              const occurrence = variantOccurrence[variantKey] ?? 0;
              variantOccurrence[variantKey] = occurrence + 1;
              const key = item.variant_id ? `${item.variant_id}_${occurrence}` : `${item.item_id}_${index}`;
              return (
                <li
                  key={key}
                  className={`py-1.5 ${item.is_freebie ? "px-1.5" : "px-3"} ${
                    isUnservicable ? "!px-0" : ""
                  }`}>
                  <ItemCard
                    {...item}
                    disableOOSItems={disableOOSItems}
                    checkoutItemsMutable={checkoutItemsMutable}
                    isUnservicable={isUnservicable}
                    isItemPropertiesAvailable={Boolean(isItemPropertiesAvailable)}
                    blockVarientSelector={blockVarientSelector}
                    itemLevelAddons={getItemLevelAddOnsForItemIndex(
                      availableAddOns,
                      item.itemIndex ?? index,
                      items,
                    )}
                  />
                </li>
              );
            });
          })()}
        </ul>
        {showFullScreenLoader && <OverlaySpinner />}
      </>
    );
  },
);

export default CheckoutItems;
